import React from 'react';
import { Search } from 'lucide-react';
import { cn } from './uiTheme';

/**
 * Shared search input. Icon on the left; Enter or optional search button triggers onSearch.
 * @param {string} [value]
 * @param {function} [onChange] - (value: string) => void
 * @param {function} [onSearch] - (value: string) => void - called when search is submitted (Enter or button click)
 * @param {string} [placeholder]
 * @param {'default'|'compact'} [variant='default'] - compact matches toolbar controls (e.g. refresh button height)
 * @param {boolean} [disabled=false]
 */
function SearchBar({
  value = '',
  onChange,
  onSearch,
  placeholder = 'Search…',
  className = '',
  variant = 'default',
  disabled = false,
  ...props
}: any) {
  const handleSubmit = (e) => {
    e?.preventDefault?.();
    onSearch?.(value ?? '');
  };

  const isCompact = variant === 'compact';

  const formClass = cn(
    'flex items-center border border-dark-border bg-dark-elevated',
    'focus-within:border-brand-purple focus-within:ring-1 focus-within:ring-brand-purple/30',
    'transition-all duration-200 ease-out origin-center',
    !disabled && 'motion-safe:hover:border-dark-border-glow',
    isCompact ? 'gap-1.5 rounded-lg min-h-[2.25rem] py-0' : 'gap-2 rounded-xl',
    disabled && 'opacity-60 pointer-events-none',
    className
  );

  const iconWrapClass = cn(
    'flex-shrink-0 text-slate-500 pointer-events-none',
    isCompact ? 'pl-2.5' : 'pl-3.5'
  );

  const inputClass = cn(
    'flex-1 min-w-0 pr-3.5 text-sm bg-transparent border-0',
    'text-slate-100 placeholder:text-slate-500',
    'focus:outline-none focus:ring-0',
    isCompact ? 'py-1.5 pl-0 rounded-r-lg leading-snug' : 'py-2.5 rounded-r-xl'
  );

  return (
    <form onSubmit={handleSubmit} className={formClass}>
      <span className={iconWrapClass} aria-hidden>
        <Search className="w-4 h-4" strokeWidth={2} />
      </span>
      <input
        type="search"
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className={inputClass}
        {...props}
        disabled={disabled}
      />
    </form>
  );
}

export default SearchBar;
